import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Anchor, Waves, Compass } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import PortThumbnail from "@/components/PortThumbnail";

/**
 * Elegant circular map popup with a **golden dial** interaction.
 *
 * • Click a location on the Journey Timeline → this dialog opens on that stop.
 * • Grab the golden ring around the map and spin it:
 *     clockwise  ↷ → advance to the next location
 *     counter    ↶ → back to the previous one
 *   Every 45° of rotation notches to the next/previous stop.
 * • While spinning, all locations appear as small circular thumbnails
 *   orbiting just outside the ring — their positions rotate in sync
 *   with the user's drag so the whole ring feels like one physical dial.
 * • The map itself (Leaflet + CartoDB Voyager tiles) re-centres and
 *   re-pins on the newly-selected location.
 *
 * Backward compatible: single-location props (name / stopType / dateLabel)
 * still work; if `stops` is provided, dial mode is enabled.
 */

const iconFor = (t) => (t === "anchor" ? Anchor : t === "passage" ? Waves : MapPin);
const labelFor = (t) => (t === "anchor" ? "At Anchor" : t === "passage" ? "At Sea" : "On Port");

const GOLDEN_PIN_HTML = `
  <div class="port-map-pin" aria-hidden>
    <span class="port-map-pin__halo"></span>
    <span class="port-map-pin__dot"></span>
  </div>
`;

function ensurePinStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("port-map-pin-style")) return;
  const el = document.createElement("style");
  el.id = "port-map-pin-style";
  el.textContent = `
    .port-map-pin { position: relative; width: 26px; height: 26px; transform: translate(-13px, -13px); }
    .port-map-pin__dot { position: absolute; inset: 6px; border-radius: 50%; background: #d4af37; border: 2px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,0.35); }
    .port-map-pin__halo { position: absolute; inset: 0; border-radius: 50%; background: rgba(212,175,55,0.32); animation: portMapHalo 2.4s ease-in-out infinite; }
    @keyframes portMapHalo { 0%,100% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.6); opacity: 0.05; } }
    .port-map-leaflet .leaflet-control-attribution { background: rgba(255,255,255,0.7); font-size: 9px; padding: 1px 6px; }
    .port-map-leaflet .leaflet-control-attribution a { color: #6b7280; }
    /* Golden dashed hint on the outer ring, visible only when the dial is active */
    .port-map-dial-hint { stroke: #d4af37; stroke-width: 1; stroke-dasharray: 4 6; fill: none; opacity: 0.55; }
  `;
  document.head.appendChild(el);
}

// Polar → cartesian with 0° at 12 o'clock, clockwise positive.
function polarPos(angleDeg, radius) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: radius * Math.cos(rad), y: radius * Math.sin(rad) };
}

const STEP = 45; // degrees per notch

export default function PortMapDialog({ open, onClose, stops, initialIndex = 0, name, stopType, dateLabel }) {
  // Normalise input — array-mode when caller passes `stops`, single-stop
  // fallback otherwise so older call-sites still render.
  const stopList = useMemo(() => {
    if (Array.isArray(stops) && stops.length) return stops;
    if (name) return [{ id: "solo", port: name, at: null, stop_type: stopType }];
    return [];
  }, [stops, name, stopType]);

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [ringRotation, setRingRotation] = useState(0); // cumulative visual rotation degrees
  const [isSpinning, setIsSpinning] = useState(false);
  // Dial mode is HIDDEN by default and activated by long-press on the
  // title chip — mobile testers couldn't reliably find the thin ring on
  // iPad, so we make the activation explicit + intentional.
  const [spinModeActive, setSpinModeActive] = useState(false);
  const [state, setState] = useState({ loading: true, lat: null, lon: null });
  const [wrapperSize, setWrapperSize] = useState(420);

  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const chipRef = useRef(null);
  const mapRef = useRef(null);
  const mapMarkerRef = useRef(null);
  const drag = useRef({ down: false, lastAngle: 0, accum: 0, timer: null });
  const longPressTimer = useRef(null);
  const longPressStart = useRef({ x: 0, y: 0 });

  // Angular step between orbiting thumbnails — evenly divide the ring so
  // any number of stops (4, 7, 12…) sits at consistent spacing.
  const step = stopList.length > 1 ? 360 / stopList.length : 45;

  // Reset every time the popup re-opens. Auto-activate the dial when
  // there's more than one stop so all destinations project around the
  // golden ring immediately — no compass-tap required. Reads as a
  // smartwatch/Digital-Crown experience the moment the map opens.
  useEffect(() => {
    if (open) {
      setActiveIndex(initialIndex);
      setRingRotation(0);
      setIsSpinning(false);
      setSpinModeActive(stopList.length > 1);
      drag.current = { down: false, lastAngle: 0, accum: 0, timer: null };
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    }
  }, [open, initialIndex, stopList.length]);

  // Auto-hide the dial after longer inactivity — resets whenever the
  // active index changes (any spin activity keeps it alive). 12 s gives
  // guests time to browse without the ring vanishing under them.
  useEffect(() => {
    if (!spinModeActive) return;
    const t = setTimeout(() => { setSpinModeActive(false); setIsSpinning(false); }, 12000);
    return () => clearTimeout(t);
  }, [spinModeActive, activeIndex]);

  const activeStop = stopList[activeIndex];
  const activeName = activeStop?.port || "";
  const activeType = activeStop?.stop_type || "port";
  const activeCountry = activeStop?.country || "";
  const activeLat = activeStop?.lat;
  const activeLon = activeStop?.lon;
  const activeDate = activeStop?.at
    ? new Date(activeStop.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : dateLabel;

  // Geocode fetch — re-runs whenever the active stop changes. Prefers the
  // manager-picked lat/lon on the stop (locked to the exact place), only
  // hitting the geocode API when they're missing. Country param narrows
  // Nominatim's search so "Portofino, Italy" doesn't drift to a namesake.
  //
  // Crucially, when a fresh fetch is in flight we **keep the previous
  // lat/lon** in state (functional setState) — the map stays visible on
  // the old location and simply pans to the new one the moment coords
  // land. Prior versions cleared lat/lon to null on every hop, which
  // flipped the container visibility off and flashed a gray/beige circle
  // between destinations.
  useEffect(() => {
    if (!open || !activeName) return;
    let live = true;
    if (activeLat != null && activeLon != null) {
      setState({ loading: false, lat: activeLat, lon: activeLon });
      return () => { live = false; };
    }
    setState((s) => ({ loading: true, lat: s.lat, lon: s.lon }));
    const q = new URLSearchParams({ name: activeName });
    if (activeCountry) q.set("country", activeCountry);
    api
      .get(`/public/geocode?${q.toString()}`)
      .then(({ data }) => {
        if (!live) return;
        if (data?.lat != null && data?.lon != null) {
          setState({ loading: false, lat: data.lat, lon: data.lon });
        } else {
          // Failed to resolve — keep the prior map view, just clear the
          // loading flag so the corner spinner disappears.
          setState((s) => ({ loading: false, lat: s.lat, lon: s.lon }));
        }
      })
      .catch(() => { if (live) setState((s) => ({ loading: false, lat: s.lat, lon: s.lon })); });
    return () => { live = false; };
  }, [open, activeName, activeCountry, activeLat, activeLon]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Measure the wrapper — orbit radius depends on it.
  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const update = () => wrapperRef.current && setWrapperSize(wrapperRef.current.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [open]);

  // Leaflet lifecycle — the map is initialised ONCE per popup open and
  // subsequent destination changes trigger a smooth `flyTo` instead of
  // destroying and rebuilding the canvas. That's what makes the dial
  // feel smartwatch-like: the map is a single continuous surface that
  // glides between locations, not a hard cut on every jump.
  useEffect(() => {
    ensurePinStyles();
    if (!open) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        mapMarkerRef.current = null;
      }
      return undefined;
    }
    if (mapRef.current || !containerRef.current || state.lat == null || state.lon == null) {
      return undefined;
    }
    const map = L.map(containerRef.current, {
      center: [state.lat, state.lon],
      zoom: 7,
      zoomControl: false,
      scrollWheelZoom: true,
      dragging: true,
      doubleClickZoom: true,
      keyboard: false,
      attributionControl: true,
      zoomSnap: 0.5,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);
    const pin = L.divIcon({ className: "port-map-pin-wrap", html: GOLDEN_PIN_HTML, iconSize: [0, 0], iconAnchor: [0, 0] });
    mapMarkerRef.current = L.marker([state.lat, state.lon], { icon: pin, keyboard: false, interactive: false }).addTo(map);
    setTimeout(() => map.invalidateSize(true), 250);
    mapRef.current = map;
    return undefined;
  }, [open, state.lat, state.lon]);

  // Fast pan on active-destination change — `panTo` keeps the current
  // zoom (no zoom-out-then-zoom-in seesaw of flyTo) so a dial drag
  // scrubs across destinations without wave-shaped motion. Duration is
  // short (0.35 s) so the pin feels tethered to the dial position.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || state.lat == null || state.lon == null) return;
    map.panTo([state.lat, state.lon], { animate: true, duration: 0.35, easeLinearity: 0.4, noMoveStart: true });
    if (mapMarkerRef.current) mapMarkerRef.current.setLatLng([state.lat, state.lon]);
  }, [state.lat, state.lon]);

  // Dial interaction — pointer events on the SVG torus hitbox.
  const getAngle = (ev) => {
    if (!wrapperRef.current) return 0;
    const r = wrapperRef.current.getBoundingClientRect();
    return (
      (Math.atan2(ev.clientY - (r.top + r.height / 2), ev.clientX - (r.left + r.width / 2)) * 180) / Math.PI
    );
  };
  const onDialDown = (e) => {
    if (stopList.length < 2 || !spinModeActive) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    drag.current.down = true;
    drag.current.lastAngle = getAngle(e);
    drag.current.accum = 0;
    if (drag.current.timer) { clearTimeout(drag.current.timer); drag.current.timer = null; }
    setIsSpinning(true);
  };
  const onDialMove = (e) => {
    if (!drag.current.down) return;
    const angle = getAngle(e);
    let delta = angle - drag.current.lastAngle;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    drag.current.lastAngle = angle;
    drag.current.accum += delta;
    setRingRotation((r) => r + delta);
    while (drag.current.accum >= step) {
      drag.current.accum -= step;
      setActiveIndex((i) => (i + 1) % stopList.length);
    }
    while (drag.current.accum <= -step) {
      drag.current.accum += step;
      setActiveIndex((i) => (i - 1 + stopList.length) % stopList.length);
    }
  };
  const onDialUp = () => {
    if (!drag.current.down) return;
    drag.current.down = false;
    // Snap to nearest notch for a clean settle — 700 ms grace so the
    // guest can still see their landing before the thumbnails hide.
    setRingRotation((r) => Math.round(r / step) * step);
    setIsSpinning(false);
  };

  // Long-press activates the dial. Half-second hold on the title chip
  // reveals the orbiting thumbnails and enables the drag-to-spin gesture.
  //
  // iPad note: React's synthetic pointer events on iOS Safari can't
  // reliably block the OS text-selection callout — `preventDefault()`
  // needs to be called on the *native* touchstart with a non-passive
  // listener. So we bind touch handlers directly to the chip DOM node
  // and only fall back to pointer/mouse events for desktop.
  const clearLongPress = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };
  useEffect(() => {
    const el = chipRef.current;
    if (!el || stopList.length < 2) return undefined;

    const activate = () => {
      setSpinModeActive(true);
      setRingRotation(0);
      longPressTimer.current = null;
    };
    const armFromTouch = (ev) => {
      if (!ev.touches || ev.touches.length !== 1) return;
      ev.preventDefault();               // suppresses iOS callout + scroll
      const t = ev.touches[0];
      longPressStart.current = { x: t.clientX, y: t.clientY };
      clearLongPress();
      longPressTimer.current = setTimeout(activate, 450);
    };
    const armFromMouse = (ev) => {
      ev.preventDefault();
      longPressStart.current = { x: ev.clientX, y: ev.clientY };
      clearLongPress();
      longPressTimer.current = setTimeout(activate, 450);
    };
    const drift = (ev) => {
      // Tolerate slight jitter, cancel only on a clear finger travel.
      const t = (ev.touches && ev.touches[0]) || ev;
      if (t == null) return;
      const dx = t.clientX - longPressStart.current.x;
      const dy = t.clientY - longPressStart.current.y;
      if (dx * dx + dy * dy > 400) clearLongPress();
    };

    el.addEventListener("touchstart", armFromTouch, { passive: false });
    el.addEventListener("touchmove", drift, { passive: true });
    el.addEventListener("touchend", clearLongPress);
    el.addEventListener("touchcancel", clearLongPress);
    el.addEventListener("mousedown", armFromMouse);
    el.addEventListener("mousemove", drift);
    el.addEventListener("mouseup", clearLongPress);
    el.addEventListener("mouseleave", clearLongPress);
    el.addEventListener("contextmenu", (e) => e.preventDefault());
    return () => {
      el.removeEventListener("touchstart", armFromTouch);
      el.removeEventListener("touchmove", drift);
      el.removeEventListener("touchend", clearLongPress);
      el.removeEventListener("touchcancel", clearLongPress);
      el.removeEventListener("mousedown", armFromMouse);
      el.removeEventListener("mousemove", drift);
      el.removeEventListener("mouseup", clearLongPress);
      el.removeEventListener("mouseleave", clearLongPress);
      clearLongPress();
    };
  }, [open, stopList.length]);

  const Icon = iconFor(activeType);
  // Orbit radius grows when the dial is active so all destinations sit
  // comfortably around a larger golden ring — no crowding on charters
  // with 10+ stops. Idle state uses a tighter ring so nothing peeks out.
  const orbitR = spinModeActive
    ? wrapperSize / 2 + Math.max(48, Math.min(96, 22 + stopList.length * 3))
    : wrapperSize / 2 + 22;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={`fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm ${spinModeActive ? (isSpinning ? "cursor-grabbing" : "cursor-grab") : "cursor-default"}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onPointerDown={spinModeActive ? onDialDown : undefined}
          onPointerMove={spinModeActive ? onDialMove : undefined}
          onPointerUp={spinModeActive ? onDialUp : undefined}
          onPointerCancel={spinModeActive ? onDialUp : undefined}
          data-testid="port-map-backdrop"
        >
          <motion.div
            ref={wrapperRef}
            className="relative"
            style={{ width: "min(88vmin, 560px)", height: "min(88vmin, 560px)" }}
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            data-testid="port-map-dialog"
          >
            {/* Golden border + aura — rotates with the dial so guests feel it as one physical object. */}
            <motion.div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{ boxShadow: "0 0 60px 8px rgba(212,175,55,0.35), 0 0 0 3px #d4af37, 0 0 0 6px rgba(212,175,55,0.18)" }}
              animate={{ rotate: ringRotation }}
              transition={drag.current.down ? { duration: 0 } : { type: "tween", duration: 0.35, ease: "easeOut" }}
              aria-hidden
            />
            {/* Slow shimmer pulse */}
            <motion.div
              className="absolute -inset-2 rounded-full pointer-events-none"
              style={{ boxShadow: "0 0 40px 12px rgba(230,185,90,0.45)" }}
              animate={{ opacity: [0.35, 0.8, 0.35] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
              aria-hidden
            />
            {/* Circular clip that holds the map — the container div is
                ALWAYS mounted (visibility toggles only) so `containerRef`
                is available the moment lat/lon lands, preventing the
                "blank cream circle" race where the effect fired before
                the conditional render committed the ref. The map stays
                visible on the previous location while a new destination
                is being geocoded — only the corner spinner briefly hints
                that a fresh fetch is in flight. */}
            <div className="absolute inset-0 rounded-full overflow-hidden bg-[#f6f1e6]">
              <div
                ref={containerRef}
                className="port-map-leaflet w-full h-full"
                style={{ visibility: state.lat != null ? "visible" : "hidden" }}
                data-testid="port-map-canvas"
              />
              {/* Full loading state — only for the very first render, before
                  any coords have landed. Once the map is showing, we switch
                  to the tiny corner spinner instead of blanking. */}
              {state.loading && state.lat == null && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground italic" data-testid="port-map-loading">
                  Charting {activeName}…
                </div>
              )}
              {/* Missing state — no prior coords AND geocode failed. */}
              {!state.loading && state.lat == null && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center" data-testid="port-map-missing">
                  <MapPin className="h-8 w-8 text-primary/40" strokeWidth={1.4} />
                  <div className="text-sm text-muted-foreground">Couldn't chart <span className="font-medium text-foreground">{activeName}</span>.</div>
                </div>
              )}
              {/* Subtle in-place loader — appears in the top-right corner
                  of the map while a new destination geocodes, without
                  hiding the map that's already showing. */}
              {state.loading && state.lat != null && (
                <div
                  className="absolute top-3 right-3 h-6 w-6 rounded-full bg-background/85 border border-[#d4af37]/60 shadow-sm flex items-center justify-center"
                  data-testid="port-map-inline-loader"
                  aria-label="Locating next destination"
                >
                  <span className="h-2.5 w-2.5 rounded-full border-2 border-[#d4af37] border-t-transparent animate-spin" />
                </div>
              )}
            </div>

            {/* Dial hitbox — a transparent SVG torus. Only the ring band
                catches pointer events so the map inside stays fully
                pannable. */}
            {stopList.length > 1 && (
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid meet"
                style={{ pointerEvents: "none" }}
                data-testid="port-map-dial"
              >
                <path
                  d="M50,50 m-50,0 a50,50 0 1,0 100,0 a50,50 0 1,0 -100,0 M50,50 m-42,0 a42,42 0 1,1 84,0 a42,42 0 1,1 -84,0"
                  fillRule="evenodd"
                  fill="rgba(0,0,0,0.001)"
                  style={{ pointerEvents: "auto", cursor: isSpinning ? "grabbing" : "grab", touchAction: "none" }}
                  onPointerDown={onDialDown}
                  onPointerMove={onDialMove}
                  onPointerUp={onDialUp}
                  onPointerCancel={onDialUp}
                />
                {/* Dashed golden guide — visible during spin as a subtle affordance. */}
                {isSpinning && <circle cx="50" cy="50" r="46" className="port-map-dial-hint" />}
              </svg>
            )}

            {/* Orbiting thumbnails — laid out on a **fixed semi-circular
                arc** below the map: **Embark on the far left, Disembark
                on the far right**, all others distributed chronologically
                across the bottom curve. Positions are static (no rotate
                with the dial) so the guest always knows where each stop
                sits — the dial just changes WHICH one is highlighted +
                pans the map to it. Uses opacity-only motion transitions
                so framer-motion doesn't clobber our inline transform. */}
            <AnimatePresence>
              {spinModeActive && stopList.length > 1 && stopList.map((s, i) => {
                // Embark → 270° (9 o'clock in polarPos convention, i.e.
                // left of centre). Disembark → 90° (3 o'clock, right).
                // Interpolate CW through 180° (6 o'clock, straight below).
                const t = (stopList.length - 1) > 0 ? i / (stopList.length - 1) : 0.5;
                const theta = 270 - t * 180;           // 270 → 90 through 180
                const p = polarPos(theta, orbitR);
                const isActive = i === activeIndex;
                return (
                  <motion.button
                    key={s.id || `${i}-${s.port}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveIndex(i);
                      setRingRotation(0);
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="absolute focus:outline-none"
                    style={{
                      left: "50%",
                      top: "50%",
                      transform: `translate(calc(-50% + ${p.x}px), calc(-50% + ${p.y}px))`,
                    }}
                    data-testid={`port-map-orbit-thumb-${i}`}
                    aria-label={`View ${s.port}`}
                  >
                    <div className={`rounded-full ring-2 transition-all ${isActive ? "ring-[#d4af37] shadow-[0_6px_18px_-6px_rgba(212,175,55,0.6)]" : "ring-background hover:ring-[#d4af37]/60"}`}>
                      <PortThumbnail name={s.port || ""} size={isActive ? 44 : 34} />
                    </div>
                    <div className={`mt-1 text-center text-[10px] tracking-tight whitespace-nowrap max-w-[6rem] truncate mx-auto ${isActive ? "text-[#c9992b] font-medium" : "text-muted-foreground"}`}>
                      {s.port}
                    </div>
                  </motion.button>
                );
              })}
            </AnimatePresence>

            {/* Title chip — compass button beside it is the ALWAYS-WORKS
                trigger for the browsing dial (long-press on the chip stays
                as a bonus for gesture-lovers, but real users on iPad kept
                fighting iOS's text-selection callout — a plain tap on a
                button is unambiguous on every browser). */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-6 max-w-[92%] z-10 flex items-center gap-2" data-testid="port-map-title-chip">
              <div
                ref={chipRef}
                className={`inline-flex items-center gap-2 px-4 py-1.5 bg-background border shadow-sm rounded-full transition-colors select-none ${spinModeActive ? "border-[#d4af37] shadow-[0_0_0_3px_rgba(212,175,55,0.18)]" : "border-[#d4af37]/60 hover:border-[#d4af37] cursor-pointer"}`}
                role={stopList.length > 1 ? "button" : undefined}
                aria-pressed={spinModeActive || undefined}
                style={{
                  touchAction: "none",
                  WebkitTouchCallout: "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                }}
                data-testid="port-map-title-trigger"
              >
                <Icon className="h-3.5 w-3.5 text-[#c9992b]" />
                <div className="text-xs">
                  <span className="overline text-[9px] text-[#c9992b] mr-1.5">{labelFor(activeType)}</span>
                  <span className="font-display tracking-tight">{activeName}</span>
                  {activeDate && <span className="ml-2 text-muted-foreground text-[10px]">{activeDate}</span>}
                </div>
              </div>
              {stopList.length > 1 && (
                <button
                  type="button"
                  onClick={() => { setSpinModeActive((v) => !v); setRingRotation(0); }}
                  aria-label={spinModeActive ? "Hide itinerary browser" : "Browse itinerary"}
                  aria-pressed={spinModeActive}
                  className={`h-8 w-8 rounded-full border shadow-sm flex items-center justify-center transition-all ${spinModeActive ? "bg-[#d4af37] border-[#d4af37] text-white rotate-45" : "bg-background border-[#d4af37]/60 text-[#c9992b] hover:border-[#d4af37] hover:bg-[#d4af37]/5"}`}
                  data-testid="port-map-dial-toggle"
                  title="Browse the itinerary"
                >
                  <Compass className="h-4 w-4" strokeWidth={2} />
                </button>
              )}
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              className="absolute -top-2 -right-2 h-9 w-9 rounded-full bg-background border border-border shadow flex items-center justify-center hover:border-[#d4af37] transition-colors z-20"
              aria-label="Close map"
              data-testid="port-map-close"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Idle affordance — only when there's more than one stop and dial isn't active */}
            {!spinModeActive && stopList.length > 1 && (
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-9 text-[10px] text-[#c9992b]/80 italic tracking-wide whitespace-nowrap pointer-events-none" aria-hidden data-testid="port-map-hint">
                tap the compass to browse the itinerary
              </div>
            )}

            {/* Concentric outer golden ring — bigger + thicker than the
                main popup, positioned via left/top + negative margins so
                framer-motion's opacity transition doesn't override our
                centering transform (previously we used `translate(-50%,
                -50%)` inline + `animate={{scale}}`, which clobbered the
                translate and pushed the ring off to the bottom-right). */}
            {spinModeActive && stopList.length > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="absolute rounded-full pointer-events-none"
                style={{
                  width: (wrapperSize / 2 + 46) * 2,
                  height: (wrapperSize / 2 + 46) * 2,
                  left: "50%",
                  top: "50%",
                  marginLeft: -(wrapperSize / 2 + 46),
                  marginTop: -(wrapperSize / 2 + 46),
                  border: "3px solid #d4af37",
                  boxShadow: "0 0 60px 10px rgba(212,175,55,0.28), 0 0 0 1px rgba(212,175,55,0.35) inset",
                }}
                aria-hidden
                data-testid="port-map-dial-outer-ring"
              />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
